// ==========================================
// FILE: src/components/TimeVault.js
// PURPOSE: Time Vault
// - Owners create items with a release date
// - Account Managers can see locked tiles + release date (via timeVaultMeta)
// - Account Managers can only open the content after releaseAt
// - Edit/Delete requires password confirmation (reauth)
// - Contact is REQUIRED
// - Supports MULTIPLE attachments per entry (multi-select on File upload)
// ==========================================

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db, storage } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";

import {
  FiEye,
  FiFile,
  FiVideo,
  FiMic,
  FiImage,
  FiType,
  FiLock,
  FiUnlock,
  FiX,
  FiDownload,
} from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";

import PageScaffold from "./ui/PageScaffold";
import Modal from "./ui/Modal";
import VideoLightbox from "./ui/VideoLightbox";
import {
  useUserContacts,
  groupContactsForOptions,
  useAddContactAutoSelect,
  AddContactModal,
} from "../utils/contacts-ui";
import { useAccess } from "../access/AccessContext";
import {
  toStoragePath,
  openDownloadForAttachment,
  downloadTextFile,
  buildForwardMailto,
} from "../utils/managerActions";
import "../styles/layout.css";

const MAIN_COL = "timeVaultMessages"; // full content (locked for managers until release)
const META_COL = "timeVaultMeta"; // safe tiles (always visible to managers)

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toDatetimeLocalValue(date) {
  if (!date) return "";
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function fromDatetimeLocalValue(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function tsToMillis(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}

function niceDate(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
    if (!d) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

function isReleasedNow(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
    if (!d) return false;
    return Date.now() >= d.getTime();
  } catch {
    return false;
  }
}

function inferAttachmentKind(urlOrName) {
  const clean = (urlOrName || "").split("?")[0].toLowerCase();
  if (!clean) return "file";
  if (/\.(mp3|wav|m4a|aac|oga|ogg)$/i.test(clean)) return "audio";
  if (/\.(mp4|m4v|mov|webm|ogv)$/i.test(clean)) return "video";
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(clean)) return "picture";
  if (/\.(webm)$/i.test(clean)) return "video"; // default
  return "file";
}

function kindFromFile(f) {
  const t = String(f?.type || "").toLowerCase();
  if (t.startsWith("image/")) return "picture";
  if (t.startsWith("audio/")) return "audio";
  if (t.startsWith("video/")) return "video";
  // If browser didn't provide mimeType, infer from extension
  return inferAttachmentKind(f?.name || "") || "file";
}

function safeFilename(name) {
  return String(name || "attachment")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .slice(0, 120);
}

function buildAttId(prefix = "att") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
  return (first + last).toUpperCase();
}

function normalizeAttachmentsFromDoc(docData) {
  const arr = Array.isArray(docData?.attachments) ? docData.attachments.filter(Boolean) : [];
  if (arr.length) {
    return arr
      .map((a) => ({
        url: a.url || a.fileUrl || null,
        path: a.path || a.filePath || null,
        name: a.name || a.fileName || "Attachment",
        kind: a.kind || a.attachmentKind || (a.url ? inferAttachmentKind(a.url) : "file"),
      }))
      .filter((a) => !!a.url || !!a.path);
  }
  // Back-compat single attachment fields
  if (docData?.fileUrl || docData?.filePath) {
    return [
      {
        url: docData.fileUrl || null,
        path: docData.filePath || null,
        name: docData.fileName || "Attachment",
        kind: docData.attachmentKind || (docData.fileUrl ? inferAttachmentKind(docData.fileUrl) : "file"),
      },
    ];
  }
  return [];
}

async function deleteStorageByPathOrUrl({ filePath, fileUrl }) {
  const derivedFromUrl = (() => {
    try {
      const enc = fileUrl?.split("/o/")[1]?.split("?")[0] || "";
      return enc ? decodeURIComponent(enc) : null;
    } catch {
      return null;
    }
  })();
  const path = filePath || derivedFromUrl;
  if (!path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch {
    // ignore
  }
}

export default function TimeVault({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };
  const ownerUid = user?.uid;

  const [tiles, setTiles] = useState([]);
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [error, setError] = useState("");

  // View modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null); // tile/meta
  const [viewFull, setViewFull] = useState(null); // full content after release

  // Editor modal
  const [showEditor, setShowEditor] = useState(false);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [releaseAtLocal, setReleaseAtLocal] = useState("");
  const [composeKind, setComposeKind] = useState("text");
  const [saving, setSaving] = useState(false);

  // Attachments draft list (existing + newly selected)
  // item shape:
  // { id, isNew, kind, name, url, path, file, previewUrl }
  const [draftAtt, setDraftAtt] = useState([]);
  const originalExistingRef = useRef([]); // snapshot of existing attachments when opening editor

  // Contacts
  const { contacts } = useUserContacts(user);
  const grouped = useMemo(() => groupContactsForOptions(contacts), [contacts]);
  const [forContactId, setForContactId] = useState("");
  const [forContactName, setForContactName] = useState("");
  const onContactCreated = (c) => {
    setForContactId(c.id);
    setForContactName(c.name);
  };
  const {
    open: showContactModal,
    setOpen: setShowContactModal,
    openModal: openAddContact,
  } = useAddContactAutoSelect(contacts, onContactCreated);

  // Help
  const [showVideo, setShowVideo] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  // Password confirm modal
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const [pendingAction, setPendingAction] = useState(null); // {type:'edit'|'delete', item}

  // Attachment picker modal
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState("file");

  // Hidden file inputs
  const messageRef = useRef(null);
  const fileUploadRef = useRef(null);
  const imageUploadRef = useRef(null);
  const imageCaptureRef = useRef(null);
  const audioUploadRef = useRef(null);
  const audioCaptureRef = useRef(null);
  const videoUploadRef = useRef(null);
  const videoCaptureRef = useRef(null);

  // LISTEN: owners -> full docs; managers -> meta tiles
  // No orderBy to avoid composite index.
  useEffect(() => {
    if (!ownerUid) return undefined;
    setError("");

    const col = readOnly ? META_COL : MAIN_COL;
    const q = query(collection(db, col), where("uid", "==", ownerUid));

    return onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data(), __meta: readOnly }));
        rows.sort((a, b) => {
          const am = tsToMillis(a.created) || tsToMillis(a.updated) || tsToMillis(a.releaseAt);
          const bm = tsToMillis(b.created) || tsToMillis(b.updated) || tsToMillis(b.releaseAt);
          return bm - am;
        });
        setTiles(rows);
      },
      (err) => setError(err?.message || "Failed to load Time Vault")
    );
  }, [ownerUid, readOnly]);

  function resetEditor() {
    setActive(null);
    setText("");
    setReleaseAtLocal("");
    setComposeKind("text");
    setDraftAtt([]);
    originalExistingRef.current = [];
    setSaving(false);
    setError("");
    setForContactId("");
    setForContactName("");
  }

  function openNew() {
    if (readOnly) return;
    if (free && tiles.length >= 1) { setShowUpgrade(true); return; }
    resetEditor();
    const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    setReleaseAtLocal(toDatetimeLocalValue(d));
    setShowEditor(true);
  }

  function closeEditor() {
    setShowEditor(false);
    resetEditor();
  }

  function openEditorForExisting(fullItem) {
    if (readOnly) return;
    setActive(fullItem);
    setText(fullItem?.text || "");
    const d = fullItem?.releaseAt?.toDate ? fullItem.releaseAt.toDate() : null;
    setReleaseAtLocal(toDatetimeLocalValue(d));
    setForContactId(fullItem?.forContactId || "");
    setForContactName(fullItem?.forContactName || "");
    setComposeKind("text");

    const existing = normalizeAttachmentsFromDoc(fullItem).map((a) => ({
      id: buildAttId("existing"),
      isNew: false,
      kind: a.kind || "file",
      name: a.name || "Attachment",
      url: a.url || null,
      path: a.path || null,
      file: null,
      previewUrl: a.url || "",
    }));

    originalExistingRef.current = existing.map((x) => ({ url: x.url, path: x.path, name: x.name, kind: x.kind }));
    setDraftAtt(existing);

    setShowEditor(true);
    requestAnimationFrame(() => messageRef.current?.focus({ preventScroll: true }));
  }

  function handleForChange(e) {
    const val = e.target.value;
    if (val === "__ADD__") {
      // keep selection unset until contact is added
      setForContactId("");
      setForContactName("");
      openAddContact();
      return;
    }
    setForContactId(val);
    const sel = contacts.find((c) => c.id === val);
    setForContactName(sel ? sel.name : "");
  }

  function handleInputChange(e) {
    const list = Array.from((e.target && e.target.files) || []);
    if (!list.length) {
      setPickerOpen(false);
      return;
    }

    const newItems = list.map((f) => {
      const k = kindFromFile(f);
      let prev = "";
      try {
        // Only generate object URLs for media to avoid unnecessary memory usage
        if (k === "picture" || k === "audio" || k === "video") prev = URL.createObjectURL(f);
      } catch {
        prev = "";
      }
      return {
        id: buildAttId("new"),
        isNew: true,
        kind: k,
        name: f.name || "Attachment",
        url: null,
        path: null,
        file: f,
        previewUrl: prev,
      };
    });

    setDraftAtt((prev) => [...prev, ...newItems]);

    // reset input so selecting the same files again triggers onChange
    try {
      e.target.value = "";
    } catch {
      // ignore
    }

    setPickerOpen(false);
  }

  function pickKind(kind) {
    if (readOnly) return;
    setComposeKind(kind);
    setError("");

    if (kind === "text") {
      setPickerOpen(false);
      requestAnimationFrame(() => messageRef.current?.focus({ preventScroll: true }));
      return;
    }
    setPickerKind(kind);
    setPickerOpen(true);
  }

  function removeDraftAttachment(id) {
    setDraftAtt((prev) => prev.filter((a) => a.id !== id));
  }

  function removeAllDraftAttachments() {
    setDraftAtt([]);
  }

  async function saveMeta(docId, payload, attachmentsFinal, createdTs) {
    // Safe fields only (NO text / NO fileUrl / NO filePath)
    const count = Array.isArray(attachmentsFinal) ? attachmentsFinal.length : 0;
    const primaryKind = count ? (attachmentsFinal[0]?.kind || "file") : "text";

    const meta = {
      uid: payload.uid,
      releaseAt: payload.releaseAt,
      ...(createdTs ? { created: createdTs } : {}),
      updated: payload.updated || serverTimestamp(),
      attachmentKind: primaryKind,
      attachmentCount: count,
      hasAttachment: count > 0,
      forContactId: payload.forContactId || null,
      forContactName: payload.forContactName || null,
    };
    await setDoc(doc(db, META_COL, docId), meta, { merge: true });
  }

  async function handleSave(e) {
    e.preventDefault();
    if (readOnly) return;

    // Contact is compulsory
    if (!forContactId) {
      setError("Please choose a contact.");
      return;
    }

    const releaseDate = fromDatetimeLocalValue(releaseAtLocal);
    if (!releaseDate) {
      setError("Please set a valid release date.");
      return;
    }

    // Require either text or at least one attachment
    const hasAnyAttachment = draftAtt.length > 0;
    if (!text.trim() && !hasAnyAttachment) {
      setError("Add some text or attach a file.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const isNew = !active;
      const docRef = isNew ? doc(collection(db, MAIN_COL)) : doc(db, MAIN_COL, active.id);
      const docId = docRef.id;

      // Existing attachments at time editor opened
      const original = Array.isArray(originalExistingRef.current) ? originalExistingRef.current : [];

      const keepExisting = draftAtt
        .filter((a) => !a.isNew)
        .map((a) => ({ url: a.url || null, path: a.path || null, name: a.name || "Attachment", kind: a.kind || "file" }))
        .filter((a) => !!a.url || !!a.path);

      // Determine which originals were removed
      const removed = original.filter((o) => {
        return !keepExisting.some((k) => (k.path && o.path && k.path === o.path) || (!k.path && !o.path && k.url && o.url && k.url === o.url));
      });

      // Delete removed originals from storage (best-effort)
      for (const r of removed) {
        await deleteStorageByPathOrUrl({ filePath: r.path, fileUrl: r.url });
      }

      // Upload new attachments
      const newOnes = draftAtt.filter((a) => a.isNew && a.file);
      const uploaded = [];

      for (const a of newOnes) {
        const f = a.file;
        const safe = safeFilename(f.name);
        const fileName = `${Date.now()}_${safe}`;
        const filePath = `${MAIN_COL}/${ownerUid}/${docId}/${fileName}`;
        const sref = storageRef(storage, filePath);
        const task = uploadBytesResumable(sref, f);
        await new Promise((res, rej) => task.on("state_changed", null, rej, res));
        const url = await getDownloadURL(task.snapshot.ref);
        uploaded.push({ url, path: filePath, name: f.name || fileName, kind: kindFromFile(f) });
      }

      const attachmentsFinal = [...keepExisting, ...uploaded];

      const primary = attachmentsFinal[0] || null;

      const payload = {
        uid: ownerUid,
        text: text || "",
        releaseAt: Timestamp.fromDate(releaseDate),
        // new schema
        attachments: attachmentsFinal,
        // back-compat single fields
        fileUrl: primary ? primary.url : null,
        fileName: primary ? primary.name : null,
        filePath: primary ? primary.path : null,
        attachmentKind: primary ? primary.kind : "text",
        forContactId: forContactId || null,
        forContactName: forContactName || null,
        updated: serverTimestamp(),
      };

      if (isNew) {
        const created = serverTimestamp();
        await setDoc(docRef, { ...payload, created });
        await saveMeta(docId, payload, attachmentsFinal, created);
      } else {
        await updateDoc(docRef, payload);
        await saveMeta(docId, payload, attachmentsFinal, null);
      }

      closeEditor();
    } catch (err) {
      setError(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  function requestPassword(actionType, item) {
    setPw("");
    setPwErr("");
    setPendingAction({ type: actionType, item });
    setPwOpen(true);
  }

  async function confirmPassword() {
    setPwErr("");
    const u = auth.currentUser;
    if (!u) {
      setPwErr("You are not logged in.");
      return;
    }
    const hasPasswordProvider = (u.providerData || []).some((p) => p.providerId === "password");
    if (!hasPasswordProvider || !u.email) {
      setPwErr("Password confirmation is only available for email/password accounts.");
      return;
    }
    if (!pw) {
      setPwErr("Please enter your account password.");
      return;
    }

    setPwBusy(true);
    try {
      const cred = EmailAuthProvider.credential(u.email, pw);
      await reauthenticateWithCredential(u, cred);

      setPwOpen(false);
      setPw("");

      const pa = pendingAction;
      setPendingAction(null);
      if (!pa?.item) return;

      if (pa.type === "edit") openEditorForExisting(pa.item);
      if (pa.type === "delete") await deleteItemNow(pa.item);
    } catch (err) {
      setPwErr(err?.message || "Password confirmation failed");
    } finally {
      setPwBusy(false);
    }
  }

  async function deleteItemNow(fullItem) {
    if (readOnly) return;
    if (!fullItem?.id) return;
    if (!window.confirm("Delete this Time Vault item?")) return;

    try {
      const allAtt = normalizeAttachmentsFromDoc(fullItem);
      for (const a of allAtt) {
        await deleteStorageByPathOrUrl({ filePath: a.path, fileUrl: a.url });
      }
      // also delete legacy single fields if present and not in attachments
      if (fullItem.filePath || fullItem.fileUrl) {
        await deleteStorageByPathOrUrl({ filePath: fullItem.filePath, fileUrl: fullItem.fileUrl });
      }

      await deleteDoc(doc(db, MAIN_COL, fullItem.id));
      await deleteDoc(doc(db, META_COL, fullItem.id));

      setViewOpen(false);
      setViewItem(null);
      setViewFull(null);
    } catch (err) {
      alert(err?.message || "Failed to delete");
    }
  }

  async function openView(tileOrItem) {
    if (!tileOrItem) return;
    setError("");
    setViewItem(tileOrItem);
    setViewFull(null);

    // Owners already have full content
    if (!readOnly) {
      setViewFull(tileOrItem);
      setViewOpen(true);
      return;
    }

    // Managers: tile always, full only after release
    const released = isReleasedNow(tileOrItem.releaseAt);
    if (!released) {
      setViewOpen(true);
      return;
    }

    try {
      const snap = await getDoc(doc(db, MAIN_COL, tileOrItem.id));
      if (!snap.exists()) {
        setError("This item is not available yet.");
        setViewOpen(true);
        return;
      }
      const full = { id: snap.id, ...snap.data() };
      setViewFull(full);
      setViewOpen(true);
    } catch {
      setError("This item is locked until its release date.");
      setViewOpen(true);
    }
  }

  async function downloadOne(att) {
    const pathOrUrl = att?.path || att?.url;
    if (!pathOrUrl) return;
    await openDownloadForAttachment({
      ownerUid,
      pathOrUrl,
      suggestedName: att?.name || "attachment",
    });
  }

  const actionsDesktop = (
    <>
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </>
  );

  const actionsMobile = (
    <div className="dp-help-row">
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );

  const viewAttachments = useMemo(() => {
    const src = viewFull || viewItem;
    return normalizeAttachmentsFromDoc(src);
  }, [viewFull, viewItem]);

  return (
    <>
      <PageScaffold
        title="Time Vault"
        onAdd={!readOnly ? openNew : undefined}
        heroSrc="/lastwish-account-Personal-Mesages.jpg"
        heroAlt="Time Vault"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
        description={
          <div className="dp-intro">
            {readOnly
              ? "These items are time-locked. You can see who they are for and the release date now, and open the message once the release date has passed."
              : "Create a message now and choose a release date. Your Account Manager can see the tile immediately, but can only open it after the release date."}
          </div>
        }
      >
        {/* Hidden inputs */}
        <input
          ref={fileUploadRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleInputChange}
        />

        <input
          ref={imageUploadRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
        <input
          ref={imageCaptureRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={handleInputChange}
        />

        <input
          ref={audioUploadRef}
          type="file"
          accept="audio/*"
          multiple
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
        <input
          ref={audioCaptureRef}
          type="file"
          accept="audio/*"
          capture="microphone"
          style={{ display: "none" }}
          onChange={handleInputChange}
        />

        <input
          ref={videoUploadRef}
          type="file"
          accept="video/*"
          multiple
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
        <input
          ref={videoCaptureRef}
          type="file"
          accept="video/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={handleInputChange}
        />

        {error ? (
          <div className="dp-error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="dv-list" data-allow-readonly="1">
          {tiles.length === 0 ? (
            <div className="empty-state">
              {readOnly
                ? "When the owner creates Time Vault items, you’ll see them here with their release dates."
                : "No Time Vault items yet. Click + to create one."}
            </div>
          ) : null}

          {tiles.map((t) => {
            const released = isReleasedNow(t.releaseAt);
            const lockedForManager = readOnly && !released;

            // Prefer meta fields when manager
            const count =
              typeof t.attachmentCount === "number"
                ? t.attachmentCount
                : Array.isArray(t.attachments)
                ? t.attachments.length
                : t.hasAttachment
                ? 1
                : t.fileUrl
                ? 1
                : 0;

            const primaryKind =
              t.attachmentKind ||
              (Array.isArray(t.attachments) && t.attachments[0]?.kind) ||
              (t.fileUrl ? inferAttachmentKind(t.fileUrl) : "text");

            const firstLine = !readOnly ? String(t.text || "").split(/\r?\n/)[0].trim() : "";
            const title = firstLine || "Time Vault";

            const whoName = t.forContactName || "";
            const whoInitials = initialsFromName(whoName);

            const icon =
              primaryKind === "file"
                ? <FiFile />
                : primaryKind === "video"
                ? <FiVideo />
                : primaryKind === "audio"
                ? <FiMic />
                : primaryKind === "picture"
                ? <FiImage />
                : <FiType />;

            const badge = released ? (
              <span className="dp-pill ok"><FiUnlock /> Released</span>
            ) : (
              <span className="dp-pill lock"><FiLock /> Locked</span>
            );

            return (
              <div key={t.id} className="card dv-item">
                <div className="dv-left">
                  <div className={`tv-thumb ${lockedForManager ? "locked" : ""}`} aria-hidden="true">
                    <div className="tv-thumb-ico">{icon}</div>
                    {count > 1 ? <div className="tv-thumb-count">{count}</div> : null}
                    {whoInitials ? <div className="tv-thumb-initials">{whoInitials}</div> : null}
                  </div>
                </div>

                <div className="dv-main">
                  <div className="tile-title" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span>{title}</span>
                    {badge}
                  </div>

                  {/* Contact (now always expected) */}
                  <div className="tile-sub">
                    <b>For:</b> {t.forContactName || "—"}
                  </div>

                  <div className="tile-sub">
                    <b>Release:</b> {niceDate(t.releaseAt)}
                  </div>

                  <div className="tile-sub">
                    {count > 0 ? `Attachments: ${count}` : "Text message"}
                  </div>
                </div>

                <div className="dv-actions">
                  <button
                    type="button"
                    className={`btn btn-main ${lockedForManager ? "btn-disabled" : ""}`}
                    onClick={() => openView(t)}
                    title={lockedForManager ? "Locked until release date" : "View"}
                    {...(readOnly ? { "data-allow-readonly": "1" } : {})}
                  >
                    View
                  </button>

                  {!readOnly && t.__meta === false ? (
                    <button type="button" className="btn btn-danger" onClick={() => requestPassword("delete", t)}>
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {/* View modal */}
        <Modal
          open={viewOpen}
          title="Time Vault"
          onClose={() => {
            setViewOpen(false);
            setViewItem(null);
            setViewFull(null);
          }}
          footer={
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {!readOnly && viewFull ? (
                <>
                  <button className="btn btn-main" onClick={() => requestPassword("edit", viewFull)} type="button">
                    Edit
                  </button>
                  <button className="btn btn-danger" onClick={() => requestPassword("delete", viewFull)} type="button">
                    Delete
                  </button>
                </>
              ) : null}
              <button className="btn btn-cancel" onClick={() => setViewOpen(false)} type="button" data-allow-readonly>
                Close
              </button>
            </div>
          }
        >
          {!viewItem ? null : (
            <div className="dp-view">
              <div className="dp-view-meta">
                <div>
                  <b>For:</b> {viewItem.forContactName || "—"}
                </div>
                <div>
                  <b>Release:</b> {niceDate(viewItem.releaseAt)}
                </div>
              </div>

              {readOnly && !isReleasedNow(viewItem.releaseAt) ? (
                <div className="dp-locked">
                  <FiLock /> Locked until the release date.
                </div>
              ) : !viewFull ? (
                <div className="dp-locked">
                  <FiLock /> Loading…
                </div>
              ) : (
                <>
                  {viewFull.text ? <div className="dp-view-text">{viewFull.text}</div> : null}

                  {viewAttachments.length ? (
                    <div className="dp-view-attach">
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Attachments</div>

                      <div style={{ display: "grid", gap: 12 }}>
                        {viewAttachments.map((a, idx) => {
                          const kind = a.kind || (a.url ? inferAttachmentKind(a.url) : "file");
                          return (
                            <div key={`${a.path || a.url || idx}`} className="card" style={{ padding: 12 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                <div style={{ fontWeight: 800 }}>
                                  {idx + 1}. {a.name || "Attachment"} <span style={{ color: "#6b7280", fontWeight: 700 }}>({kind})</span>
                                </div>
                                <button type="button" className="btn btn-main" onClick={() => downloadOne(a)} data-allow-readonly>
                                  <FiDownload /> Download
                                </button>
                              </div>

                              <div style={{ marginTop: 10 }}>
                                {kind === "picture" && a.url ? (
                                  <img src={a.url} alt="attachment" className="dp-img" />
                                ) : null}

                                {kind === "audio" && a.url ? (
                                  <audio controls src={a.url} style={{ width: "100%" }} />
                                ) : null}

                                {kind === "video" && a.url ? (
                                  <video controls src={a.url} style={{ width: "100%" }} />
                                ) : null}

                                {kind === "file" ? (
                                  <div className="dp-file-pill" style={{ marginTop: 6 }}>
                                    <FiFile /> File attached
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {readOnly ? (
                        <div style={{ marginTop: 12 }}>
                          <button
                            type="button"
                            className="btn"
                            onClick={() => {
                              const first = viewAttachments[0];
                              const path = first ? (toStoragePath(first.path || first.url) || "") : "";
                              const mailto = buildForwardMailto({
                                subject: "Time Vault item",
                                body: `Here is the Time Vault item:\n\n${viewFull.text || ""}\n\nRelease: ${niceDate(viewFull.releaseAt)}\nFor: ${viewFull.forContactName || ""}\n\nFirst attachment: ${first?.url || ""}\n(Storage path: ${path || ""})`,
                              });
                              window.location.href = mailto;
                            }}
                            data-allow-readonly
                          >
                            Forward
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                      {viewFull.text ? (
                        <button
                          type="button"
                          className="btn btn-main"
                          onClick={() => downloadTextFile("time-vault.txt", viewFull.text)}
                          data-allow-readonly
                        >
                          Download text
                        </button>
                      ) : null}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Modal>

        {/* Editor modal (owner only) */}
        <Modal
          open={showEditor}
          title={active ? "Edit Time Vault" : "New Time Vault"}
          onClose={closeEditor}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <button className="btn btn-cancel" type="button" onClick={closeEditor}>
                Cancel
              </button>
              <button className="btn btn-main" type="button" disabled={saving} onClick={handleSave}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          }
        >
          <form onSubmit={handleSave}>
            <div className="dp-field">
              <label>Release date</label>
              <input type="datetime-local" value={releaseAtLocal} onChange={(e) => setReleaseAtLocal(e.target.value)} />
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                Your Account Manager cannot open this entry until this date.
              </div>
            </div>

            <div className="dp-field">
              <label>Assign to <span style={{ color: "#9f1239" }}>*</span></label>
              <select value={forContactId} onChange={handleForChange} required>
                <option value="" disabled>
                  Choose a contact
                </option>
                <option value="__ADD__">+ Add new contact</option>
                {grouped.map((g) => (
                  <optgroup key={g.key} label={g.label}>
                    {g.items.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="dp-field">
              <label>Message / Attachment</label>

              <div className="pm-kind-grid" style={{ marginTop: 8 }}>
                <button type="button" className={`pm-kind ${composeKind === "file" ? "active" : ""}`} onClick={() => pickKind("file")}>
                  <FiFile className="pm-kind-ico" />
                  <span>File</span>
                </button>
                <button type="button" className={`pm-kind ${composeKind === "video" ? "active" : ""}`} onClick={() => pickKind("video")}>
                  <FiVideo className="pm-kind-ico" />
                  <span>Video</span>
                </button>
                <button type="button" className={`pm-kind ${composeKind === "audio" ? "active" : ""}`} onClick={() => pickKind("audio")}>
                  <FiMic className="pm-kind-ico" />
                  <span>Audio</span>
                </button>
                <button type="button" className={`pm-kind ${composeKind === "picture" ? "active" : ""}`} onClick={() => pickKind("picture")}>
                  <FiImage className="pm-kind-ico" />
                  <span>Picture</span>
                </button>
                <button type="button" className={`pm-kind ${composeKind === "text" ? "active" : ""}`} onClick={() => pickKind("text")}>
                  <FiType className="pm-kind-ico" />
                  <span>Text</span>
                </button>
              </div>

              <textarea
                ref={messageRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write your message…"
                rows={6}
                style={{ marginTop: 10 }}
              />

              {/* Attachments list */}
              {draftAtt.length ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ fontWeight: 900 }}>Attachments ({draftAtt.length})</div>
                    <button type="button" className="btn btn-cancel" onClick={removeAllDraftAttachments}>
                      Remove all
                    </button>
                  </div>

                  <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                    {draftAtt.map((a) => (
                      <div key={a.id} className="card" style={{ padding: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <div style={{ fontWeight: 800 }}>
                            {a.name} <span style={{ color: "#6b7280", fontWeight: 700 }}>({a.kind})</span>
                            {a.isNew ? <span style={{ marginLeft: 8, fontSize: 12, color: "#065f46", fontWeight: 900 }}>(new)</span> : null}
                          </div>
                          <button type="button" className="btn btn-danger" onClick={() => removeDraftAttachment(a.id)}>
                            <FiX /> Remove
                          </button>
                        </div>

                        <div style={{ marginTop: 10 }}>
                          {a.kind === "picture" && (a.previewUrl || a.url) ? (
                            <img src={a.previewUrl || a.url} alt="preview" className="dp-img" />
                          ) : null}
                          {a.kind === "audio" && (a.previewUrl || a.url) ? (
                            <audio controls src={a.previewUrl || a.url} style={{ width: "100%" }} />
                          ) : null}
                          {a.kind === "video" && (a.previewUrl || a.url) ? (
                            <video controls src={a.previewUrl || a.url} style={{ width: "100%" }} />
                          ) : null}
                          {a.kind === "file" ? (
                            <div className="dp-file-pill" style={{ marginTop: 6 }}>
                              <FiFile /> File selected
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Picker modal */}
            <Modal
              open={pickerOpen}
              title="Choose"
              onClose={() => setPickerOpen(false)}
              footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button className="btn btn-cancel" type="button" onClick={() => setPickerOpen(false)}>
                    Close
                  </button>
                </div>
              }
            >
              <div className="dp-picker">
                {pickerKind === "file" ? (
                  <>
                    <button className="btn btn-main" type="button" onClick={() => fileUploadRef.current?.click()}>
                      Upload file(s)
                    </button>
                    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                      You can select multiple files here.
                    </div>
                  </>
                ) : null}

                {pickerKind === "picture" ? (
                  <>
                    <button className="btn btn-main" type="button" onClick={() => imageUploadRef.current?.click()}>
                      Upload picture(s)
                    </button>
                    <button className="btn" type="button" onClick={() => imageCaptureRef.current?.click()}>
                      Take picture
                    </button>
                    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                      Tip: you can select multiple images on supported devices.
                    </div>
                  </>
                ) : null}

                {pickerKind === "audio" ? (
                  <>
                    <button className="btn btn-main" type="button" onClick={() => audioUploadRef.current?.click()}>
                      Upload audio file(s)
                    </button>
                    <button className="btn" type="button" onClick={() => audioCaptureRef.current?.click()}>
                      Record audio
                    </button>
                    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                      Tip: you can select multiple audio files on supported devices.
                    </div>
                  </>
                ) : null}

                {pickerKind === "video" ? (
                  <>
                    <button className="btn btn-main" type="button" onClick={() => videoUploadRef.current?.click()}>
                      Upload video file(s)
                    </button>
                    <button className="btn" type="button" onClick={() => videoCaptureRef.current?.click()}>
                      Record video
                    </button>
                    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                      Tip: you can select multiple videos on supported devices.
                    </div>
                  </>
                ) : null}

                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                  Tip: On mobile, “Record” uses your device microphone/camera.
                </div>
              </div>
            </Modal>
          </form>
        </Modal>

        {/* Password confirm modal */}
        <Modal
          open={pwOpen}
          title="Confirm your password"
          onClose={() => setPwOpen(false)}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <button className="btn btn-main" type="button" onClick={confirmPassword} disabled={pwBusy}>
                {pwBusy ? "Checking…" : "Confirm"}
              </button>
              <button className="btn btn-cancel" type="button" onClick={() => setPwOpen(false)}>
                Cancel
              </button>
            </div>
          }
        >
          <div className="dp-field">
            <label htmlFor="tv-pw">Password</label>
            <input
              id="tv-pw"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Enter your account password"
              autoComplete="current-password"
            />
            {pwErr ? (
              <div className="dp-error" role="alert" style={{ marginTop: 10 }}>
                {pwErr}
              </div>
            ) : null}
          </div>
          <div style={{ color: "#6b7280", fontSize: 12, marginTop: 8, lineHeight: 1.45 }}>
            This protects Time Vault edits/deletes. Your password is never stored.
          </div>
        </Modal>

        {/* Add contact modal */}
        <AddContactModal open={showContactModal} onClose={() => setShowContactModal(false)} user={user} />

        {/* Read how it works drawer */}
        {showDrawer ? (
          <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
            <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="dp-drawer-head">
                <div className="dp-drawer-title">How Time Vault works</div>
                <div className="dp-drawer-tools">
                  <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly>
                    Print
                  </button>
                  <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly>
                    Close
                  </button>
                </div>
              </div>
              <div className="dp-drawer-body">
                <ol>
                  <li>
                    <b>Create:</b> Add a message and/or attachments, and set the <b>release date</b>.
                  </li>
                  <li>
                    <b>Locked tiles:</b> Account Managers can see who it’s for and the release date, but can’t open it early.
                  </li>
                  <li>
                    <b>Release:</b> On the release date, the content becomes viewable to Account Managers (read-only).
                  </li>
                  <li>
                    <b>Owner control:</b> Only you can edit/delete, and you’ll be asked to confirm your password.
                  </li>
                </ol>
              </div>
            </aside>
          </div>
        ) : null}

        <VideoLightbox
          open={showVideo}
          onClose={() => setShowVideo(false)}
          url="https://youtu.be/bk96CH5Kgfs?si=IJ2NQStfdfB4Bxqg"
          title="How Time Vault works"
          startAt={0}
          captions={false}
          startMuted={false}
        />

        <style>{`
          :root{--lwb-maroon:#2a0516;--lwb-accent:#e97c13}
          .dp-error{background:#fff1f2;border:1px solid #fecdd3;color:#9f1239;padding:10px 12px;border-radius:12px;font-weight:700;margin:10px 0}

          /* Match other pages tile layout */
          .dv-list{display:grid;grid-template-columns:1fr;gap:16px}
          .dv-item{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:12px;padding:12px}
          .dv-left{width:42px}
          .tile-thumb{width:42px;height:42px;border-radius:8px;object-fit:cover;background:#f4f5f9;display:block}

          /* Time Vault tile thumb (more engaging) */
          .tv-thumb{width:46px;height:46px;border-radius:14px;position:relative;display:grid;place-items:center;
            background:linear-gradient(135deg, #2a0516 0%, #645155 100%);
            color:#fff;box-shadow:0 10px 22px rgba(0,0,0,0.14);overflow:hidden}
          .tv-thumb::after{content:"";position:absolute;inset:0;z-index:1;
            background:radial-gradient(circle at 28% 20%, rgba(233,124,19,0.35), rgba(233,124,19,0) 58%)}
          .tv-thumb-ico{position:relative;z-index:2;font-size:1.25rem;display:grid;place-items:center}
          .tv-thumb-count{position:absolute;z-index:3;top:6px;right:6px;min-width:18px;height:18px;padding:0 6px;
            border-radius:999px;background:rgba(255,255,255,0.92);color:#2a0516;font-size:11px;font-weight:900;
            display:flex;align-items:center;justify-content:center;line-height:1}
          .tv-thumb-initials{position:absolute;z-index:3;bottom:6px;left:6px;font-size:10px;font-weight:900;
            letter-spacing:.4px;background:rgba(0,0,0,0.28);padding:2px 6px;border-radius:999px}
          .tv-thumb.locked{opacity:0.86;filter:saturate(0.8)}
          .dv-actions{display:flex;gap:8px;justify-self:end}

          /* Help buttons */
          .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
          .dp-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
          .dp-help.read { background: #650202; color: #fff; }
          .dp-help.see  { background: #003353; color: #fff; }
          .dp-help-ico { font-size: 1.05rem; }
          .dp-intro { margin: 4px 0 10px; color: #2a0516; }

          /* Lock badge */
          .dp-pill{display:inline-flex;align-items:center;gap:6px;padding:5px 10px;border-radius:999px;font-weight:900;font-size:12px;border:1px solid rgba(0,0,0,0.06)}
          .dp-pill.lock{background:#fff7ed;color:#9a3412;border-color:#fed7aa}
          .dp-pill.ok{background:#ecfdf5;color:#065f46;border-color:#a7f3d0}

          .btn-disabled{opacity:.55;cursor:not-allowed}

          /* Viewer */
          .dp-view-meta{display:grid;gap:6px;color:#374151;font-size:13px;margin-bottom:10px}
          .dp-view-text{white-space:pre-wrap;background:#fafafa;border:1px solid rgba(0,0,0,0.08);border-radius:14px;padding:12px;color:#111827}
          .dp-view-attach{margin-top:12px}
          .dp-img{width:100%;max-height:340px;object-fit:cover;border-radius:14px;border:1px solid rgba(0,0,0,0.08)}
          .dp-file-pill{display:inline-flex;align-items:center;gap:8px;background:#f3f4f6;border:1px solid rgba(0,0,0,0.06);padding:10px 12px;border-radius:999px;font-weight:800;color:#111827}
          .dp-locked{display:flex;align-items:center;gap:8px;background:#fff7ed;border:1px solid #fed7aa;color:#9a3412;border-radius:14px;padding:12px;font-weight:900}

          /* Editor */
          .dp-field{margin-top:12px}
          .dp-picker{display:grid;gap:10px}
          .pm-kind-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px}
          .pm-kind{border:1px solid rgba(0,0,0,0.08);background:#fff;border-radius:14px;padding:10px 8px;display:flex;flex-direction:column;align-items:center;gap:6px;font-weight:900;color:var(--lwb-maroon);cursor:pointer;box-shadow:0 6px 18px rgba(0,0,0,0.06);transition:transform .12s ease, box-shadow .12s ease, border-color .12s ease}
          .pm-kind:hover{transform:translateY(-1px);box-shadow:0 10px 22px rgba(0,0,0,0.10);border-color:rgba(0,0,0,0.14)}
          .pm-kind:active{transform:translateY(0)}
          .pm-kind-ico{font-size:1.25rem}
          .pm-kind span{font-size:.8rem;line-height:1}
          .pm-kind.active{border-color:rgba(233,124,19,0.7);box-shadow:0 10px 22px rgba(233,124,19,0.18)}
          .pm-kind.active .pm-kind-ico{color:var(--lwb-accent)}
          @media(max-width:560px){.pm-kind-grid{grid-template-columns:repeat(3,minmax(0,1fr))}}

          /* Drawer (same as other pages) */
          :root { --topbar-h: 56px; }
          .dp-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.3); z-index:10000; display:flex; justify-content:flex-end; }
          .dp-drawer { position: fixed; top: var(--topbar-h, 56px); right: 0; height: calc(100vh - var(--topbar-h, 56px)); width: min(520px, 95vw); background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.15); border-top-left-radius: 12px; border-bottom-left-radius: 12px; display: flex; flex-direction: column; overflow: hidden; }
          .dp-drawer-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
          .dp-drawer-title { font-weight:700; color:#2a0516; }
          .dp-drawer-tools { display:flex; gap:8px; }
          .dp-drawer-body { padding:16px; overflow:auto; }
        `}</style>
      </PageScaffold>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="messages" />
    </>
  );
}
